import CryptoJS from 'crypto-js';


export interface PaperResult {
  pdf_url?: string | null;
  paper_url?: string | null;
  work_id?: string | null;
  work_id_type?: string | null;
  title: string;
  authors?: string[] | null;
  year?: number | null;
  venue?: string | null;
  identifiers?: Record<string, string> | null;
  key_terms?: string[] | null;
  confidence: number;
  source: string;
  error?: string | null;
  is_open_access?: boolean | null;
}

export interface PaperSearchRequest {
  text: string;
}

export interface HistoryItem {
  request_id: number;
  post_id: number;
  input_text: string;
  pdf_url: string | null;
  paper_url: string | null;
  work_id: string | null;
  work_id_type: string | null;
  title: string;
  authors: string[];
  year: number;
  venue: string;
  identifiers: Record<string, string>;
  key_terms: string[];
  confidence: number;
  source: string;
  is_open_access: number | null;
  success: boolean;
  error_message: string | null;
  processing_time_ms: number;
  timestamp: string;
}

export interface HistoryResponse {
  items: HistoryItem[];
  next_cursor: string | null;
  has_more: boolean;
}

const API_BASE_URL = process.env.EXPO_PUBLIC_API_URL;
const HMAC_SECRET = process.env.EXPO_PUBLIC_HMAC_SECRET;

/**
 * Creates HMAC-SHA256 signature for request authentication
 */
async function createSignature(timestamp: string, body: object): Promise<string> {
  const message = `${timestamp}:${JSON.stringify(body)}`;

  // Create proper HMAC-SHA256 signature
  const signature = CryptoJS.HmacSHA256(message, HMAC_SECRET!).toString(CryptoJS.enc.Hex);

  return signature;
}

export async function searchPapers(text: string, timeoutMs: number = 120000): Promise<PaperResult[]> {
  try {
    // Create timestamp (seconds since epoch)
    const timestamp = Math.floor(Date.now() / 1000).toString();

    // Prepare request body
    const requestBody = { text };

    // Create HMAC signature
    const signature = await createSignature(timestamp, requestBody);

    const response = await fetch(`${API_BASE_URL}/find_paper`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Signature': signature,
        'X-Timestamp': timestamp,
      },
      body: JSON.stringify(requestBody),
      signal: AbortSignal.timeout(timeoutMs),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail ||
        `API request failed: ${response.status} ${response.statusText}`
      );
    }

    const data: PaperResult | PaperResult[] = await response.json();

    // Handle both single result and array of results
    const results = Array.isArray(data) ? data : [data];

    // Filter out errors and empty results
    return results.filter(result => !result.error && result.title);
  } catch (error) {
    console.error('Error searching papers:', error);
    throw error;
  }
}

export async function fetchHistory(limit: number = 20, openAccessOnly: boolean = true): Promise<HistoryItem[]> {
  try {
    const params = new URLSearchParams({
      limit: limit.toString(),
      open_access_pdf_only: openAccessOnly.toString()
    });

    const response = await fetch(`${API_BASE_URL}/history?${params}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch history: ${response.status}`);
    }

    const data: HistoryResponse = await response.json();
    return data.items;
  } catch (error) {
    console.error('Error fetching history:', error);
    throw error;
  }
}